'use client'

import { useState, useTransition } from 'react'
import Link from 'next/link'
import { Formik, Form } from 'formik'
import { TextField, TextFieldTypes } from 'sidecar-components/atoms/TextField'
import { Button, ButtonSize } from 'sidecar-components/atoms/Button'
import { IconSize } from 'sidecar-components/atoms/Icon'
import { Card } from 'sidecar-components/atoms/Card'
import { FormErrorBubble } from './FormErrorBubble'
import {
  loginPasswordSchema,
  type LoginPasswordFormValues,
} from '@/lib/validation-schemas'
import { ROUTES } from '@/lib/constants'

/**
 * PasswordStepForm component for the first step of login flow.
 *
 * Displays email and password input fields for user authentication. This is the
 * first factor of the two-factor authentication flow.
 */
interface PasswordStepFormProps {
  onSubmit: (formData: FormData) => void
  isSubmitting: boolean
  error?: string
  testBypass?: boolean
}

export function PasswordStepForm({
  onSubmit,
  isSubmitting,
  error,
  testBypass,
}: PasswordStepFormProps) {
  const [showPassword, setShowPassword] = useState(false)

  const [isPending, startTransition] = useTransition()

  return (
    <Card
      className="relative z-1"
      childrenClass="p-2"
      footer={
        <p className="text-center">
          <strong>{"Don't have a password yet?"}</strong>{' '}
          <Link href={ROUTES.REGISTER} data-testid="link_register">
            Register
          </Link>
        </p>
      }
    >
      <>
        {/* Header */}
        <div className="mb-3">
          <h2>Login</h2>
        </div>

        {/* Error message from server */}
        <FormErrorBubble
          error={error}
          isLoading={isSubmitting}
          testId="password-error"
        />

        {/* Formik form for email + password */}
        <Formik<LoginPasswordFormValues>
          initialValues={{ email: '', password: '' }}
          validationSchema={loginPasswordSchema}
          onSubmit={(values, { setSubmitting }) => {
            const formData = new FormData()
            formData.append('email', values.email)
            formData.append('password', values.password)
            if (testBypass) formData.append('testBypass', 'true')

            startTransition(() => {
              onSubmit(formData)
              setSubmitting(false)
            })
          }}
        >
          {({
            values,
            errors,
            handleChange,
            handleSubmit,
            handleBlur,
            isSubmitting: formikSubmitting,
          }) => {
            // Determine if submit button should be disabled
            const isButtonDisabled =
              !values.email.trim() ||
              !values.password.trim() ||
              !!errors.email ||
              !!errors.password ||
              isSubmitting ||
              formikSubmitting ||
              isPending

            return (
              <Form onSubmit={handleSubmit}>
                {/* Email field */}
                <TextField
                  label="Email"
                  name="email"
                  type={TextFieldTypes.EMAIL}
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={isSubmitting}
                  placeholder=""
                  testId="input_email"
                  className="mb-8"
                />

                {/* Password field with eye toggle */}
                <div className="relative mb-8">
                  <TextField
                    label="Password"
                    name="password"
                    type={
                      showPassword
                        ? TextFieldTypes.TEXT
                        : TextFieldTypes.PASSWORD
                    }
                    value={values.password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={isSubmitting}
                    placeholder=""
                    testId="input_password"
                    button="eyeFilled"
                    buttonLabel="Toggle password visibility"
                    buttonSize={IconSize.MD}
                    onButtonClick={() => setShowPassword(!showPassword)}
                  />
                </div>

                {/* Submit button */}
                <Button
                  type="submit"
                  size={ButtonSize.LARGE}
                  width="full"
                  disabled={isButtonDisabled}
                  busy={isSubmitting || formikSubmitting}
                  icon="forward"
                  iconPosition="after"
                  testId="btn_login"
                >
                  {isSubmitting || formikSubmitting
                    ? 'Logging in...'
                    : 'Log in'}
                </Button>

                {/* Forgot password link */}
                <div className="text-center">
                  <Link
                    href={ROUTES.FORGOT_PASSWORD}
                    data-testid="link_forgotPassword"
                  >
                    Forgot password?
                  </Link>
                </div>
              </Form>
            )
          }}
        </Formik>
      </>
    </Card>
  )
}
